import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Checkbox } from "components/ui/checkbox";
import {
  BucketAmountSource,
  BudgetBucketView,
  CreateBudgetBucketDto,
  UpdateBudgetBucketDto,
} from "screens/investment/types/budget-bucket";

interface BudgetBucketFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (dto: CreateBudgetBucketDto) => Promise<unknown>;
  onUpdate?: (id: number, dto: UpdateBudgetBucketDto) => Promise<unknown>;
  buildingId: string;
  /** Pass an existing bucket to switch the form into edit mode. */
  bucket?: BudgetBucketView | null;
}

/**
 * Single modal that handles both create and edit for a budget bucket. The
 * caller passes either `onCreate` (no `bucket`) or `onUpdate` (with
 * `bucket`) — never both. Keeps the form a single component so PMs see the
 * same fields in both contexts.
 */
export const BudgetBucketFormModal = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  buildingId,
  bucket = null,
}: BudgetBucketFormModalProps) => {
  const isEdit = !!bucket;

  const [name, setName] = useState("");
  const [year, setYear] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /**
   * Laat het bedrag het gebouw volgen i.p.v. het met de hand bij te houden.
   * Het acquisitiebudget stond zo op € 152.000 terwijl bij Financieel
   * € 300.000 renovatiebudget stond — hetzelfde cijfer op twee plaatsen.
   */
  const [volgtGebouw, setVolgtGebouw] = useState(false);

  // Sync with the incoming bucket whenever the modal is (re-)opened.
  useEffect(() => {
    if (isOpen) {
      setName(bucket?.name ?? "");
      setYear(bucket?.year != null ? String(bucket.year) : "");
      setTotalAmount(
        bucket?.totalAmount != null ? String(bucket.totalAmount) : "",
      );
      setNotes(bucket?.notes ?? "");
      setVolgtGebouw(
        bucket?.amountSource ===
          BucketAmountSource.ACQUISITION_RENOVATION_BUDGET,
      );
    }
  }, [isOpen, bucket]);

  const trimmedName = name.trim();
  const parsedYear = year ? Number(year) : null;
  const parsedAmount = Number(totalAmount);
  const isValid =
    trimmedName.length > 0 &&
    // Volgt het bedrag het gebouw, dan is het invoerveld leeg en alleen-lezen —
    // dan mag een leeg veld het opslaan niet tegenhouden.
    (volgtGebouw || (!Number.isNaN(parsedAmount) && parsedAmount >= 0)) &&
    (!parsedYear || (Number.isInteger(parsedYear) && parsedYear >= 2000));

  const amountSource = volgtGebouw
    ? BucketAmountSource.ACQUISITION_RENOVATION_BUDGET
    : BucketAmountSource.MANUAL;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      // Bij "volgt het gebouw" gaat er 0 mee: de backend negeert het bedrag en
      // haalt het meteen bij het gebouw op.
      const bedrag = volgtGebouw ? 0 : parsedAmount;
      if (isEdit && bucket && onUpdate) {
        await onUpdate(bucket.id, {
          name: trimmedName,
          year: parsedYear,
          totalAmount: bedrag,
          amountSource,
          notes: notes.trim() || null,
        });
      } else if (!isEdit && onCreate) {
        await onCreate({
          buildingId,
          name: trimmedName,
          year: parsedYear,
          totalAmount: bedrag,
          amountSource,
          notes: notes.trim() || null,
        });
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Strategisch budget bewerken" : "Nieuw strategisch budget"}
          </DialogTitle>
          <DialogDescription>
            Een strategisch budget is een budget waar investeringslijnen uit
            kunnen putten. De goedkeuring beheer je apart op het budget zelf.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bucket-name">Naam</Label>
            <Input
              id="bucket-name"
              placeholder="Bv. Strategisch 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bucket-year">Jaar (optioneel)</Label>
              <Input
                id="bucket-year"
                type="number"
                placeholder="2026"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bucket-total">Totaalbedrag (€)</Label>
              <Input
                id="bucket-total"
                type="number"
                step="0.01"
                placeholder="500000"
                value={volgtGebouw ? "" : totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                // Alleen-lezen zodra het bedrag het gebouw volgt: een getal dat
                // je hier intikt zou bij de volgende lading overschreven worden.
                disabled={volgtGebouw}
              />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="bucket-follows-building"
              checked={volgtGebouw}
              onCheckedChange={(checked) => setVolgtGebouw(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="bucket-follows-building"
              className="text-neutral-secondary-mid text-xs leading-snug font-normal"
            >
              Bedrag overnemen uit{" "}
              <span className="text-neutral-primary font-medium">
                Financieel → "+ Renovatiebudget (incl. btw) bij aankoop"
              </span>{" "}
              van dit gebouw. Voor het acquisitiebudget: zo staat dat cijfer maar
              op één plaats en kan het niet uit elkaar lopen.
            </Label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bucket-notes">Notities (optioneel)</Label>
            <Textarea
              id="bucket-notes"
              placeholder="Context of beslissingsnotitie"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting
              ? "Opslaan..."
              : isEdit
                ? "Wijzigingen opslaan"
                : "Pot aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
